// Shared 8 Ball Pool Unique ID state (saved VIP id + recent ids), used by product inputs,
// deal cards, blog reward forms and the giveaway. Loaded on every page before those scripts.
(() => {
  if (window.EightBpUniqueId) return;
  const VIP_STORAGE_KEY = 'bbirdg:8bp-vip-user-id:v1';
  const VIP_RECENT_STORAGE_KEY = 'bbirdg:8bp-vip-recent-id:v1';
  const VIP_RECENT_LIMIT = 3;
  const VIP_ID_EVENT = 'eight-bp:vip-id-changed';

  const createElement = (tag, attributes = {}, text = '') => {
    const element = document.createElement(tag);
    Object.entries(attributes).forEach(([name, value]) => {
      if (value === false || value === null || value === undefined || value === '') return;
      if (value === true) element.setAttribute(name, '');
      else element.setAttribute(name, String(value));
    });
    if (text) element.textContent = text;
    return element;
  };

  const createClearButton = (label, handler, iconTemplate = null) => {
    const button = createElement('button', {
      type: 'button',
      class: 'product-input-field__clear',
      'aria-label': label,
      title: label,
      hidden: true
    });
    const icon = iconTemplate?.content?.firstElementChild?.cloneNode(true);
    if (icon) {
      icon.setAttribute('aria-hidden', 'true');
      icon.removeAttribute('width');
      icon.removeAttribute('height');
      button.append(icon);
    } else {
      button.textContent = '×';
    }
    button.addEventListener('click', handler);
    return button;
  };

  const createUniqueIdState = () => {
    const controls = new Set();

    const normalize = (value) => {
      const candidate = String(value || '').trim();
      return /^\d+$/.test(candidate) ? candidate : '';
    };

    const read = () => {
      try {
        const value = normalize(localStorage.getItem(VIP_STORAGE_KEY));
        if (!value && localStorage.getItem(VIP_STORAGE_KEY)) localStorage.removeItem(VIP_STORAGE_KEY);
        return value;
      } catch (_) {
        return '';
      }
    };

    const readRecent = () => {
      try {
        const stored = localStorage.getItem(VIP_RECENT_STORAGE_KEY)?.trim() || '';
        if (!stored) return [];
        const parsed = stored.startsWith('[') ? JSON.parse(stored) : [stored];
        if (!Array.isArray(parsed)) throw new Error('Invalid recent Unique IDs');
        const ids = Array.from(new Set(parsed.map(normalize).filter(Boolean))).slice(0, VIP_RECENT_LIMIT);
        if (!stored.startsWith('[') || ids.length !== parsed.length) {
          if (ids.length) localStorage.setItem(VIP_RECENT_STORAGE_KEY, JSON.stringify(ids));
          else localStorage.removeItem(VIP_RECENT_STORAGE_KEY);
        }
        return ids;
      } catch (_) {
        try { localStorage.removeItem(VIP_RECENT_STORAGE_KEY); } catch (_) {}
        return [];
      }
    };

    const syncControl = (control, userId) => {
      if (!control.input.isConnected) {
        controls.delete(control);
        return;
      }
      control.input.value = userId;
      control.sync();
    };

    const syncAll = (userId, source = '') => {
      controls.forEach((control) => {
        if (!control.input.isConnected) controls.delete(control);
        else if (control.source !== source) syncControl(control, userId);
        else control.sync();
      });
    };

    const broadcast = (userId, source = '') => {
      document.dispatchEvent(new CustomEvent(VIP_ID_EVENT, { detail: { source, userId } }));
    };

    const set = (value, { source = '', persist = false, remember = false, notify = true } = {}) => {
      const userId = normalize(value);
      if (!userId) return '';
      try {
        if (persist) localStorage.setItem(VIP_STORAGE_KEY, userId);
        if (remember) {
          const ids = [userId, ...readRecent().filter((id) => id !== userId)].slice(0, VIP_RECENT_LIMIT);
          localStorage.setItem(VIP_RECENT_STORAGE_KEY, JSON.stringify(ids));
        }
      } catch (_) {}
      if (notify) broadcast(userId, source);
      else syncAll(userId, source);
      return userId;
    };

    const clear = ({ source = '', notify = true } = {}) => {
      try { localStorage.removeItem(VIP_STORAGE_KEY); } catch (_) {}
      if (notify) broadcast('', source);
      else syncAll('', source);
    };

    const forgetRecent = (value) => {
      const userId = normalize(value);
      try {
        const remaining = readRecent().filter((id) => id !== userId);
        if (remaining.length) localStorage.setItem(VIP_RECENT_STORAGE_KEY, JSON.stringify(remaining));
        else localStorage.removeItem(VIP_RECENT_STORAGE_KEY);
      } catch (_) {}
      controls.forEach((control) => {
        if (control.input.isConnected) control.renderRecent();
        else controls.delete(control);
      });
    };

    const enhance = (input, options = {}) => {
      if (!input || input.uniqueIdController) return input?.uniqueIdController || null;
      const source = options.source || input.id || `eight-bp-unique-id-${controls.size + 1}`;
      const label = options.label || input.dataset.uniqueIdLabel || '';
      const clearLabel = options.clearLabel || input.dataset.uniqueIdClearLabel || '';
      const closeLabel = options.closeLabel || input.dataset.uniqueIdCloseLabel || clearLabel;
      const recentLabel = options.recentLabel || input.dataset.uniqueIdRecentLabel || '';
      const container = options.container || input.closest('.form-control-wrap') || input.parentElement;
      if (!container) return null;

      input.inputMode = 'numeric';
      input.pattern = '[0-9]+';
      container.classList.add('product-input-field__clearable');
      const iconTemplate = options.iconTemplate
        || input.closest('[data-product-input-fields]')?.querySelector('[data-product-input-clear-icon]')
        || document.querySelector('[data-product-input-clear-icon]');
      const accessibleClearLabel = [clearLabel, label].filter(Boolean).join(' ');
      const clearButton = createClearButton(accessibleClearLabel, () => {
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus({ preventScroll: true });
      }, iconTemplate);
      if (options.before) container.insertBefore(clearButton, options.before);
      else container.append(clearButton);

      let recent = null;
      let recentItems = null;
      if (options.showRecent !== false && recentLabel) {
        recent = createElement('div', { class: 'product-input-field__recent', hidden: true });
        recent.append(createElement('span', { class: 'product-input-field__recent-label' }, recentLabel));
        recentItems = createElement('div', { class: 'product-input-field__recent-items' });
        recent.append(recentItems);
        container.append(recent);
      }

      const control = {
        input,
        source,
        sync: () => {
          clearButton.hidden = !input.value.trim();
          control.renderRecent();
        },
        renderRecent: (show = recent && !recent.hidden) => {
          if (!recent || !recentItems) return;
          const ids = readRecent().filter((id) => id !== input.value.trim());
          recentItems.replaceChildren(...ids.map((id) => {
            const item = createElement('span', { class: 'product-input-field__recent-item' });
            const select = createElement('button', { type: 'button', class: 'product-input-field__recent-select' }, id);
            const dismiss = createClearButton([closeLabel, id].filter(Boolean).join(' '), () => {
              forgetRecent(id);
              input.focus({ preventScroll: true });
            }, iconTemplate);
            dismiss.hidden = false;
            dismiss.classList.add('product-input-field__recent-dismiss');
            select.addEventListener('click', () => {
              input.value = id;
              input.dispatchEvent(new Event('input', { bubbles: true }));
              set(id, { source, persist: true, remember: true, notify: false });
              recent.hidden = true;
              input.focus({ preventScroll: true });
            });
            item.append(select, dismiss);
            return item;
          }));
          recent.hidden = !show || ids.length === 0;
        }
      };
      controls.add(control);
      input.uniqueIdController = control;
      input.addEventListener('input', () => {
        const userId = normalize(input.value);
        control.sync();
        if (userId) {
          broadcast(userId, source);
        } else if (!input.value.trim()) clear({ source });
      });
      input.addEventListener('change', () => {
        const userId = normalize(input.value);
        if (userId) set(userId, { source, persist: true, remember: true });
      });
      input.addEventListener('focus', () => control.renderRecent(true));
      input.addEventListener('blur', () => window.setTimeout(() => control.renderRecent(false), 120));
      const stored = read();
      if (!input.value.trim() && stored) input.value = stored;
      control.sync();
      return control;
    };

    document.addEventListener(VIP_ID_EVENT, (event) => {
      const userId = normalize(event.detail?.userId);
      syncAll(userId, event.detail?.source || '');
    });
    window.addEventListener('storage', (event) => {
      if (event.key === VIP_STORAGE_KEY) syncAll(normalize(event.newValue));
      if (event.key === VIP_RECENT_STORAGE_KEY) controls.forEach((control) => control.renderRecent());
    });

    return {
      storageKey: VIP_STORAGE_KEY,
      recentStorageKey: VIP_RECENT_STORAGE_KEY,
      read,
      readRecent,
      set,
      clear,
      forgetRecent,
      broadcast,
      enhance
    };
  };

  window.EightBpUniqueId = createUniqueIdState();
})();
